import type { ReportSummary, Tone } from '@/state/experiments/report-summary';
import { cn } from 'helpers/cn';
import { UserAvatar } from '@/components/UserAvatar';

type ReportCardProps = { summary: ReportSummary };

const CHIP_TONE: Record<Tone, string> = {
  ok: 'border-ok-line text-ok',
  warn: 'border-warn-line text-warn',
  danger: 'border-danger-line text-danger',
  dim: 'border-line text-dim',
};

const TEXT_TONE: Record<Tone, string> = { ok: 'text-ok', warn: 'text-warn', danger: 'text-danger', dim: 'text-dim' };

type SectionLabelProps = { children: string };
const SectionLabel = ({ children }: SectionLabelProps) => <span className="micro-label text-dim">{children}</span>;

/**
 * The finished report: facts from the platform on the left, what the team said on the right.
 *
 * The same card on the Debrief tab and in the modal's review screen. The grid packs two columns
 * when there is room and stacks them when there is not, so in the modal's narrow column it reads
 * as facts first and answers second. Every unanswered part shows a dim placeholder, so the shape
 * of the finished card is always visible and what is missing is obvious.
 */
export const ReportCard = ({ summary }: ReportCardProps) => {
  const { chip, tags, headline, subline, stats, hypothesis, outcomeLine } = summary;

  return (
    <div className="grid grid-cols-[repeat(auto-fit,minmax(340px,1fr))]">
      <div className="flex min-w-0 flex-col gap-5 p-6" data-tour="debrief-result">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className={cn('rounded-pill border px-2 py-0.5 font-mono text-[10px]', CHIP_TONE[chip.tone])}>
            {chip.label}
          </span>
          {tags.map((tag) => (
            <span key={tag} className="border-line text-dim rounded-pill border px-[7px] py-0.5 font-mono text-[10px]">
              {tag}
            </span>
          ))}
        </div>

        <div className="flex flex-col gap-2">
          <h3 className="text-foreground text-[22px] leading-[1.3] font-medium tracking-[-0.015em] text-pretty">
            {headline}
          </h3>
          {subline && <p className="supporting">{subline}</p>}
        </div>

        {stats && (
          <div className="border-line grid grid-cols-[repeat(auto-fit,minmax(110px,1fr))] border-y">
            {stats.map((cell, index) => (
              <div
                key={cell.label}
                className={cn('flex min-w-0 flex-col gap-1.5 py-3', index > 0 && 'border-line border-l pl-3.5')}
              >
                <SectionLabel>{cell.label}</SectionLabel>
                <span
                  className={cn(
                    'font-mono text-[22px] font-medium',
                    cell.tone ? TEXT_TONE[cell.tone] : 'text-foreground',
                  )}
                >
                  {cell.value}
                </span>
              </div>
            ))}
          </div>
        )}

        {hypothesis && (
          <div className="flex flex-col gap-1.5">
            <div className="flex justify-between gap-2.5">
              <SectionLabel>Hypothesis</SectionLabel>
              <span className="text-dim font-mono text-[10px]">from brief</span>
            </div>
            <p className="text-foreground text-body leading-[1.5] text-pretty">{hypothesis}</p>
            <p className={cn('text-meta', TEXT_TONE[chip.tone])}>{outcomeLine}</p>
          </div>
        )}
      </div>

      <div
        className="bg-panel2 border-line flex min-w-0 flex-col gap-5 border-t p-6 md:border-t-0 md:border-l"
        data-tour="debrief-lesson"
      >
        <div className="flex flex-col gap-2.5">
          <SectionLabel>Lesson</SectionLabel>
          <p
            className={cn(
              'text-[18px] leading-[1.45] tracking-[-0.005em] text-pretty',
              summary.lesson ? 'text-foreground' : 'text-dim',
            )}
          >
            {summary.lesson ? `"${summary.lesson}"` : 'No lesson written yet.'}
          </p>
          {/* The last author: their badge and their name, so the byline reads as the same
              kind of thing as the author badges elsewhere. The name is written, so the badge
              carries no tooltip and no label of its own. */}
          {summary.byline && (
            <span className="flex items-center gap-1.5">
              {summary.author && <UserAvatar user={summary.author} size="xs" />}
              <span className="text-dim font-mono text-[10px]">{summary.byline}</span>
            </span>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <SectionLabel>Why</SectionLabel>
          <p
            className={cn(
              'text-body leading-[1.5] whitespace-pre-wrap text-pretty',
              summary.why ? 'text-foreground' : 'text-dim',
            )}
          >
            {summary.why || 'Not given.'}
          </p>
        </div>

        <div className="flex flex-col gap-2">
          <SectionLabel>Caveats</SectionLabel>
          <div className="flex flex-wrap gap-1.5">
            {summary.caveats.length > 0 ? (
              summary.caveats.map((caveat) => (
                <span key={caveat} className="border-warn-line text-warn rounded-pill border px-2.5 py-[3px] text-meta">
                  {caveat}
                </span>
              ))
            ) : (
              <span className="border-line text-dim rounded-pill border px-2.5 py-[3px] text-meta">None noted</span>
            )}
          </div>
          {summary.caveatsNote && <p className="supporting whitespace-pre-wrap">{summary.caveatsNote}</p>}
        </div>

        <div className="border-line mt-auto flex flex-col gap-1 border-t pt-4">
          <SectionLabel>Next step</SectionLabel>
          <span className={cn('text-body font-medium', summary.next ? 'text-foreground' : 'text-dim')}>
            {summary.next ?? 'Not decided yet'}
          </span>
        </div>
      </div>
    </div>
  );
};
